"use strict";
import { BarChart2, DollarSign, Home, Lock, Repeat, Zap } from "react-feather";
import { useLocation } from "react-router-dom";
import { useSporeColors } from "ui/src";
import { TOPAZ_PRODUCT_URLS } from "constants/topazProducts";
import { Bank } from "ui/src/components/icons/Bank";
import { Document } from "ui/src/components/icons/Document";
import { Hub } from "ui/src/components/icons/Hub";
import { PapersText } from "ui/src/components/icons/PapersText";
import { Swap } from "ui/src/components/icons/Swap";
import { VoteIcon } from "ui/src/components/icons/Vote";
const PRIMARY_NAV_DESTINATIONS = [
  {
    title: "Portfolio",
    href: "/portfolio",
    activePaths: ["/portfolio", "/dashboard", "/airdrop"]
  },
  {
    title: "Swap",
    href: "/swap",
    activePaths: ["/swap", "/limit", "/dca", "/send", "/privacy", "/bridge"]
  },
  {
    title: "Liquidity",
    href: "/liquidity",
    activePaths: ["/earn", "/liquidity", "/positions", "/deposit", "/stake", "/withdraw"]
  },
  {
    title: "Vote",
    href: "/vote",
    activePaths: ["/vote", "/lockrewards", "/incentives", "/create-proposal"]
  },
  {
    title: "veTOPAZ",
    href: "/locks",
    activePaths: ["/lock", "/locks", "/bonuslock", "/relays"]
  }
];
function pathMatches(pathname, prefix) {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}
export function getPrimaryNavDestinations(pathname) {
  return PRIMARY_NAV_DESTINATIONS.map(({ title, href, activePaths }) => ({
    title,
    href,
    isActive: activePaths.some((prefix) => pathMatches(pathname, prefix))
  }));
}
function useIconProps() {
  return { size: 20, color: useSporeColors().neutral2.val };
}
export const usePrimaryNav = () => {
  const props = useIconProps();
  const { pathname } = useLocation();
  const icons = {
    Portfolio: <PapersText {...props} />,
    Swap: <Swap {...props} />,
    Liquidity: <Hub {...props} />,
    Vote: <VoteIcon {...props} />,
    veTOPAZ: <Lock {...props} />
  };
  return getPrimaryNavDestinations(pathname).map((destination) => ({
    ...destination,
    icon: icons[destination.title]
  }));
};
const SECONDARY_DESTINATIONS = [
  { title: "Relays", href: "/relays", activePath: "/relays" },
  { title: "Incentives", href: "/incentives", activePath: "/incentives" },
  { title: "Migrate", href: "/migrate", activePath: "/migrate" },
  { title: "Flow", href: TOPAZ_PRODUCT_URLS.flow, external: true },
  { title: "Vaults", href: TOPAZ_PRODUCT_URLS.vaults, external: true },
  { title: "Stats", href: "https://www.topazdex.com/stats", external: true },
  { title: "Docs", href: "https://www.topazdex.com/docs", external: true },
  { title: "Home", href: "https://www.topazdex.com", external: true }
];
export function getSecondaryDestinations(pathname) {
  return SECONDARY_DESTINATIONS.map(({ title, href, external, activePath }) => ({
    title,
    href,
    target: external,
    isActive: activePath ? pathMatches(pathname, activePath) : false
  }));
}
export function getMoreMenuDestinations(pathname) {
  return getSecondaryDestinations(pathname).filter((destination) => destination.title !== "Home");
}
export function getMobileMenuDestinations(pathname) {
  return [
    ...getPrimaryNavDestinations(pathname).map((destination) => ({ ...destination, target: void 0 })),
    ...getSecondaryDestinations(pathname).filter((destination) => !destination.target)
  ];
}
const SECONDARY_ICONS = {
  Relays: (props) => <Repeat {...props} />,
  Incentives: (props) => <DollarSign {...props} />,
  Migrate: (props) => <Repeat {...props} />,
  Flow: (props) => <Zap {...props} />,
  Vaults: (props) => <Bank {...props} />,
  Stats: (props) => <BarChart2 {...props} />,
  Docs: (props) => <Document {...props} />,
  Home: (props) => <Home {...props} />
};
export const useSecondaryDestinations = () => {
  const props = useIconProps();
  const { pathname } = useLocation();
  return getSecondaryDestinations(pathname).map((destination) => ({
    ...destination,
    icon: SECONDARY_ICONS[destination.title]?.(props)
  }));
};
export const useMoreMenu = () => useSecondaryDestinations().filter((destination) => destination.title !== "Home");
export const useMobileMenuContent = () => {
  const primary = usePrimaryNav();
  const secondary = useSecondaryDestinations();
  return [...primary, ...secondary.filter((destination) => !destination.target)];
};
